export type TaskListDropIndicator = {
    top: number;
    left: number;
};

export type TaskListDropTarget = {
    taskListElement: HTMLElement;
    taskIndex: number;
    taskItemRow: HTMLElement;
    insertAfter: boolean;
    // Denotes that the task items that follow should become children / descendants of the dropped item
    reparentsFollowingTaskItems: boolean;
};

const NESTED_DROP_INDICATOR_OFFSET = 15;

/**
 * Gets the single-line content rect without including the row's nested task subtree.
 */
const getTaskItemLineRect = (row: HTMLElement): DOMRect =>
    (
        row.querySelector<HTMLElement>(
            ':scope > .task-item-main > .task-item-content > [data-node-view-content-react] > p',
        ) ?? row
    ).getBoundingClientRect();

/**
 * Gets the task-list ancestors that contain a row, ordered from the top-level list to its own list.
 */
const getTaskListAncestors = (row: HTMLElement): HTMLElement[] => {
    const taskListElements: HTMLElement[] = [];
    let taskListElement = row.closest('li')?.parentElement;

    while (taskListElement instanceof HTMLElement) {
        taskListElements.unshift(taskListElement);
        taskListElement = taskListElement.closest('li')?.parentElement;
    }

    return taskListElements;
};

/**
 * Gets the direct task-item child of a list that contains the given row.
 */
const getTaskItemInList = ({
    row,
    taskListElement,
}: {
    row: HTMLElement;
    taskListElement: HTMLElement;
}): HTMLElement | null => {
    let taskItemElement = row.closest('li');

    while (taskItemElement && taskItemElement.parentElement !== taskListElement) {
        taskItemElement = taskItemElement.parentElement?.closest('li') ?? null;
    }

    return taskItemElement;
};

/**
 * Gets an insertion target at a task depth relative to one of the gap's surrounding rows.
 */
const getTaskListDropTargetAtDepth = ({
    row,
    depth,
    insertAfter,
}: {
    row: HTMLElement;
    depth: number;
    insertAfter: boolean;
}): TaskListDropTarget | null => {
    const taskListElement = getTaskListAncestors(row)[depth - 1];

    if (!taskListElement) return null;

    const taskItemElement = getTaskItemInList({ row, taskListElement });

    if (!taskItemElement) return null;

    const taskIndex = Array.from(taskListElement.children).indexOf(taskItemElement);
    const taskItemRow = taskItemElement.querySelector<HTMLElement>(':scope > .task-item-row');

    if (taskIndex === -1 || !taskItemRow) return null;

    return {
        taskListElement,
        taskIndex: insertAfter ? taskIndex + 1 : taskIndex,
        taskItemRow,
        insertAfter,
        reparentsFollowingTaskItems: false,
    };
};

/**
 * Gets the inclusive depth range available at an insertion gap.
 */
const getDropDepthRange = ({
    previousDepth,
    nextDepth,
}: {
    previousDepth: number;
    nextDepth: number;
}): [number, number] => {
    if (!nextDepth) return [1, previousDepth];
    if (!previousDepth) return [nextDepth, nextDepth];

    return [Math.min(previousDepth, nextDepth), Math.max(previousDepth, nextDepth)];
};

/**
 * Gets the insertion target for one available depth in an insertion gap.
 */
const getDropTargetAtDepth = ({
    previousRow,
    nextRow,
    previousDepth,
    depth,
}: {
    previousRow: HTMLElement | undefined;
    nextRow: HTMLElement | undefined;
    previousDepth: number;
    depth: number;
}): TaskListDropTarget | null => {
    if (previousRow && depth <= previousDepth) {
        return getTaskListDropTargetAtDepth({ row: previousRow, depth, insertAfter: true });
    }

    if (nextRow) return getTaskListDropTargetAtDepth({ row: nextRow, depth, insertAfter: false });

    return null;
};

/**
 * Gets the structurally valid insertion targets for the depths traversed by an insertion gap.
 */
const getTaskListDropTargets = ({
    previousRow,
    nextRow,
}: {
    previousRow: HTMLElement | undefined;
    nextRow: HTMLElement | undefined;
}): TaskListDropTarget[] => {
    const previousDepth = previousRow ? getTaskListAncestors(previousRow).length : 0;
    const nextDepth = nextRow ? getTaskListAncestors(nextRow).length : 0;
    const [firstDepth, lastDepth] = getDropDepthRange({ previousDepth, nextDepth });
    const dropTargets: TaskListDropTarget[] = [];

    for (let depth = firstDepth; depth <= lastDepth; depth++) {
        const dropTarget = getDropTargetAtDepth({ previousRow, nextRow, previousDepth, depth });

        if (dropTarget) {
            dropTargets.push({
                ...dropTarget,
                reparentsFollowingTaskItems: !!nextRow && dropTarget.insertAfter && nextDepth > depth,
            });
        }
    }

    return dropTargets;
};

/**
 * Gets the horizontal drop-target band containing the pointer.
 */
const getDropTargetBand = ({
    clientX,
    lineRect,
    dropTargetCount,
}: {
    clientX: number;
    lineRect: DOMRect;
    dropTargetCount: number;
}): number => {
    if (lineRect.width <= 0) return dropTargetCount - 1;

    const relativeX = (clientX - lineRect.left) / lineRect.width;
    return Math.min(dropTargetCount - 1, Math.max(0, Math.floor(relativeX * dropTargetCount)));
};

/**
 * Chooses one of the insertion gap's valid depths from equal horizontal bands across the row content.
 */
export const getTaskListDropTarget = ({
    rows,
    insertionIndex,
    clientX,
}: {
    rows: HTMLElement[];
    insertionIndex: number;
    clientX: number;
}): TaskListDropTarget | null => {
    const nextRow = rows[insertionIndex];
    const previousRow = rows[insertionIndex - 1];
    const referenceRow = nextRow ?? previousRow;

    if (!referenceRow) return null;

    const dropTargets = getTaskListDropTargets({ previousRow, nextRow });

    if (!dropTargets.length) return null;

    const lineRect = getTaskItemLineRect(referenceRow);
    const horizontalBand = getDropTargetBand({ clientX, lineRect, dropTargetCount: dropTargets.length });

    return dropTargets[horizontalBand];
};

/**
 * Finds the first row whose midpoint is below the client Y coordinate.
 *
 * Binary search (log₂(N)) - lower-bound search over row midpoint Y positions.
 */
export const getTaskItemInsertionIndex = ({ rows, clientY }: { rows: HTMLElement[]; clientY: number }): number => {
    let startIndex = 0;
    let endIndex = rows.length;

    while (startIndex < endIndex) {
        const middleIndex = Math.floor((startIndex + endIndex) / 2);
        const lineRect = getTaskItemLineRect(rows[middleIndex]);

        if (clientY < lineRect.top + lineRect.height / 2) {
            endIndex = middleIndex;
        } else {
            startIndex = middleIndex + 1;
        }
    }

    return startIndex;
};

/**
 * Gets a nested task list's horizontal offset from the editor's top-level task list.
 */
const getTaskListIndent = (taskListElement: HTMLElement): number => {
    const editorElement = taskListElement.closest('.ProseMirror');
    const topLevelTaskListElement = editorElement?.querySelector<HTMLElement>(':scope > ul[data-type="taskList"]');

    if (!topLevelTaskListElement) return 0;

    return taskListElement.getBoundingClientRect().left - topLevelTaskListElement.getBoundingClientRect().left;
};

/**
 * Gets the rendered scale between the drop target's local CSS pixels and viewport pixels.
 */
const getDropTargetScale = ({
    dropTargetElement,
    dropTargetRect,
}: {
    dropTargetElement: HTMLElement;
    dropTargetRect: DOMRect;
}): number => {
    if (!dropTargetElement.offsetWidth) return 1;

    return dropTargetRect.width / dropTargetElement.offsetWidth || 1;
};

/**
 * Gets the indicator's local horizontal position, leaving space for a nested task's checkbox.
 */
const getDropIndicatorLeft = ({
    taskListIndent,
    dropTargetScale,
}: {
    taskListIndent: number;
    dropTargetScale: number;
}): number => {
    // Top level drop indicators should show across the length of the card
    if (!taskListIndent) return 0;

    return taskListIndent / dropTargetScale + NESTED_DROP_INDICATOR_OFFSET;
};

/**
 * Gets the row whose edge shows the selected drop target's insertion indicator.
 */
const getDropIndicatorRow = ({
    nextRow,
    referenceRow,
    taskListDropTarget,
}: {
    nextRow: HTMLElement | undefined;
    referenceRow: HTMLElement;
    taskListDropTarget?: TaskListDropTarget | null;
}): HTMLElement => {
    if (!taskListDropTarget) return referenceRow;
    if (taskListDropTarget.reparentsFollowingTaskItems) return nextRow ?? taskListDropTarget.taskItemRow;

    return taskListDropTarget.taskItemRow;
};

/**
 * Gets the viewport Y coordinate for the selected drop target's insertion indicator.
 */
const getDropIndicatorTop = ({
    nextRow,
    rowRect,
    taskListDropTarget,
}: {
    nextRow: HTMLElement | undefined;
    rowRect: DOMRect;
    taskListDropTarget?: TaskListDropTarget | null;
}): number => {
    if (taskListDropTarget?.reparentsFollowingTaskItems) return rowRect.top;
    if (taskListDropTarget) return taskListDropTarget.insertAfter ? rowRect.bottom : rowRect.top;

    return nextRow ? rowRect.top : rowRect.bottom;
};

/**
 * Gets the line position for the insertion gap at the specified row index.
 */
export const getTaskListDropIndicator = ({
    rows,
    insertionIndex,
    dropTargetElement,
    taskListDropTarget,
}: {
    rows: HTMLElement[];
    insertionIndex: number;
    dropTargetElement: HTMLElement | null;
    taskListDropTarget?: TaskListDropTarget | null;
}): TaskListDropIndicator | null => {
    if (!dropTargetElement) return null;

    const nextRow = rows[insertionIndex];
    const previousRow = rows[insertionIndex - 1];
    const referenceRow = nextRow ?? previousRow;

    if (!referenceRow) return null;

    const taskListElement = taskListDropTarget?.taskListElement ?? referenceRow.closest('li')?.parentElement;

    if (!(taskListElement instanceof HTMLElement)) return null;

    const dropTargetRect = dropTargetElement.getBoundingClientRect();
    const indicatorRow = getDropIndicatorRow({ nextRow, referenceRow, taskListDropTarget });
    const rowRect = indicatorRow.getBoundingClientRect();
    const dropTargetScale = getDropTargetScale({ dropTargetElement, dropTargetRect });
    const indicatorTop = getDropIndicatorTop({ nextRow, rowRect, taskListDropTarget });

    return {
        // DOM rects are viewport pixels after the canvas transform, while an absolutely
        // positioned child uses the drop target's unscaled local CSS pixel coordinate space.
        top: (indicatorTop - dropTargetRect.top) / dropTargetScale,
        // The top-level line starts at the card edge; nested lists retain their
        // horizontal offset and leave space for the nested checkbox.
        left: getDropIndicatorLeft({
            taskListIndent: getTaskListIndent(taskListElement),
            dropTargetScale,
        }),
    };
};
